import { partsMapFrom, type PartsMap } from '~/deck15/morph/parts';

/**
 * A demo slide as pixels: the morph's target texture and the directory's thumbnail.
 *
 * A slide in the preview is inline SVG, so the page loads its fonts and photos. The same SVG drawn as an
 * image loads nothing from outside itself, so before it is rasterised every `url(/…)` and `href="/…"` it
 * points at is fetched once and inlined. Without that the morph resolved onto a picture with no photos
 * and fallback type, then jumped to the real slide when the veil lifted.
 */

/** `MorphCanvas`'s fixed backing store. The texture matches it so the resolve samples 1:1. */
const BACKING_W = 1024;
const BACKING_H = 576;

export interface MorphTexture {
  canvas: HTMLCanvasElement;
  parts: PartsMap;
}

const assets = new Map<string, Promise<string>>();

/** One asset as a data URI, fetched once per page. A failed fetch keeps the path, which draws nothing. */
function dataUri(url: string): Promise<string> {
  const hit = assets.get(url);
  if (hit) return hit;
  const job = fetch(url)
    .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(String(res.status)))))
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        }),
    )
    .catch(() => url);
  assets.set(url, job);
  return job;
}

const LOCAL_URL = /url\((\/[^)'"]+)\)/g;
const LOCAL_HREF = /href="(\/[^"]+)"/g;

/** The slide with every font and photo it points at inlined, so it draws the same as an image. */
async function inlineAssets(svg: string): Promise<string> {
  const urls = new Set([...svg.matchAll(LOCAL_URL), ...svg.matchAll(LOCAL_HREF)].map((m) => m[1]));
  const inlined = new Map(await Promise.all([...urls].map(async (url) => [url, await dataUri(url)] as const)));
  return svg
    .replace(LOCAL_URL, (_, url: string) => `url(${inlined.get(url) ?? url})`)
    .replace(LOCAL_HREF, (_, url: string) => `href="${inlined.get(url) ?? url}"`);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('slide did not draw'));
    img.src = src;
  });
}

const textures = new Map<string, MorphTexture>();
const thumbnails = new Map<string, string>();
const pending = new Map<string, Promise<MorphTexture | null>>();

/**
 * Rasterises a slide once: its morph texture with the reading-order parts map, and a JPEG thumbnail.
 * Resolves to null when the browser will not draw it, in which case the veil lifts onto the DOM without
 * a staggered landing and the directory keeps the SVG chip.
 */
export function slideTexture(key: string, svg: string): Promise<MorphTexture | null> {
  const hit = textures.get(key);
  if (hit) return Promise.resolve(hit);
  const inFlight = pending.get(key);
  if (inFlight) return inFlight;

  const job = (async () => {
    try {
      const inlined = await inlineAssets(svg);
      const img = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(inlined)}`);
      // The fonts inside an image decode on their own clock; give them a frame before drawing.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const canvas = document.createElement('canvas');
      canvas.width = BACKING_W;
      canvas.height = BACKING_H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, BACKING_W, BACKING_H);
      ctx.drawImage(img, 0, 0, BACKING_W, BACKING_H);
      const texture = { canvas, parts: partsMapFrom(ctx.getImageData(0, 0, BACKING_W, BACKING_H)) };
      textures.set(key, texture);
      thumbnails.set(key, `url("${canvas.toDataURL('image/jpeg', 0.82)}")`);
      return texture;
    } catch {
      return null;
    }
  })();
  pending.set(key, job);
  return job;
}

export function textureFor(key: string): MorphTexture | null {
  return textures.get(key) ?? null;
}

/** The slide's raster as a CSS `image` value, once `slideTexture` has drawn it. */
export function thumbnailFor(key: string): string | undefined {
  return thumbnails.get(key);
}
